// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { getFunctions } from "firebase/functions";

// Firebase configurations for different environments
const firebaseConfigs = {
  test: {
    apiKey: "AIzaSyAwwNCIzUxOBBQTDUPhBa7zvnjYphioGEw",
    authDomain: "wishlist-app-v.firebaseapp.com",
    projectId: "wishlist-app-v",
    storageBucket: "wishlist-app-v.firebasestorage.app",
    messagingSenderId: "308895432700",
    appId: "1:308895432700:web:b6702a9e5022dbd0b57c79"
  },
  
  prod: {
    apiKey: "AIzaSyC3jv2REdkQeMcIHnKxDFUe9ccSpzfFSJc",
    authDomain: "wispin-prod.firebaseapp.com",
    projectId: "wispin-prod",
    storageBucket: "wispin-prod.firebasestorage.app",
    messagingSenderId: "533480733924",
    appId: "1:533480733924:web:834f38c2a553c8bf25c0f1",
    measurementId: "G-NXZZ57PY02"
  }
};

// Determine which config to use based on environment
const getEnvironment = () => {
  const hostname = window?.location?.hostname;
  
  // Production: ONLY when accessing wispin.co domain  
  if (hostname === 'wispin.co' || hostname === 'www.wispin.co') {
    return 'prod';
  }
  
  // Development/Test: localhost, Vercel (wishlist-spin.vercel.app), etc.
  return 'test';
};

// Get the appropriate Firebase configuration
const firebaseConfig = firebaseConfigs[getEnvironment()];

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore with persistent local cache (IndexedDB).
// On second+ app opens, data is served from cache instantly then synced in background.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});

// Initialize Cloud Functions
export const functions = getFunctions(app);

// Initialize Firebase Storage
export const storage = getStorage(app);

// Initialize Firebase Analytics (only if supported - requires measurementId)
// Note: Analytics is only available on production (wispin.co) which has measurementId
let analyticsInstance = null;

// Initialize analytics synchronously if measurementId exists
if (firebaseConfig.measurementId) {
  try {
    analyticsInstance = getAnalytics(app);
    console.log('✅ Firebase Analytics initialized for environment:', getEnvironment());
    console.log('📊 Analytics ready - events will be tracked');
  } catch (error) {
    console.warn('⚠️ Firebase Analytics initialization failed:', error);
  }
} else {
  console.log('ℹ️ Firebase Analytics not initialized - no measurementId in config');
  console.log('ℹ️ Environment:', getEnvironment());
}

// Export analytics instance (will be null if not supported)
export const analytics = analyticsInstance;

// Expose analytics to window for console debugging
// This allows testing from browser console with: window.testAnalytics('test_event', { test: true })
if (typeof window !== 'undefined' && analyticsInstance) {
  window.__FIREBASE_ANALYTICS__ = analyticsInstance;

  // Helper function to test analytics from console
  window.testAnalytics = async (eventName, params = {}) => {
    try {
      const { logEvent } = await import('firebase/analytics');
      logEvent(analyticsInstance, eventName, params);
      console.log('✅ Analytics event logged:', eventName, params);
      console.log('💡 Check Firebase Console → Analytics → DebugView to see the event');
      console.log('💡 Make sure you have ?firebase_debug=true in the URL');
      return true;
    } catch (error) {
      console.error('❌ Failed to log analytics event:', error);
      return false;
    }
  };

  console.log('🔧 Debug helper available: window.testAnalytics("event_name", { param: "value" })');
}

// Google Auth Provider with consistent settings across environments
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account' // This forces the account selection popup
});

// Helper function to generate unique share tokens
export const generateShareToken = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

// Helper function to validate share tokens (basic validation)
export const isValidShareToken = (token) => {
  return token && typeof token === 'string' && token.length >= 20;
};